import { FormContainer } from '@leedpulse-monorepo/components';
import { Flex, Form, Input, Select } from 'antd';
import SignupFormContext from '../../../contexts/forms/SignupFormContext';
import React, { useContext, useEffect, useRef, useState } from 'react';

interface StepPersonalInfoProps {
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  stepTitle: string;
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

// TODO : @MrWarzo centraliser
interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
  maxDigits: number;
}

// TODO : @MrWarzo centraliser
const COUNTRIES: Country[] = [
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷', maxDigits: 9 },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪', maxDigits: 11 },
  { code: 'CH', name: 'Suisse', dialCode: '+41', flag: '🇨🇭', maxDigits: 9 },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧', maxDigits: 10 },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪', maxDigits: 9 },
];

const StepPersonalInfo: React.FC<StepPersonalInfoProps> = ({
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onPhoneChange,
  stepTitle,
}) => {
  const { data } = useContext(SignupFormContext);
  const { formData } = data;
  const trimmedEmail = formData.email.trim();
  const showError = trimmedEmail.length > 0 && !isValidEmail(trimmedEmail);

  const [selectedCountry, setSelectedCountry] = useState<Country>(COUNTRIES[0]);
  const [localNumber, setLocalNumber] = useState('');
  const isInitialized = useRef(false);

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);

    const truncatedNumber = localNumber.slice(0, country.maxDigits);
    setLocalNumber(truncatedNumber);
    onPhoneChange(country.dialCode + truncatedNumber);
  };

  const handleNumberChange = (value: string) => {
    let cleanValue = "";
    cleanValue = value.replace(/\D/g, ''); // Replace all non-digit characters
    cleanValue = cleanValue.replace(/^0/g, ''); // Replace the first character if == 0

    const limitedValue = cleanValue.slice(0, selectedCountry.maxDigits);
    setLocalNumber(limitedValue);
    onPhoneChange(selectedCountry.dialCode + limitedValue);
  };

  useEffect(() => {
    if (!isInitialized.current && formData.phone) {
      isInitialized.current = true;
      const country = COUNTRIES.find((c) => formData.phone.startsWith(c.dialCode));
      if (country) {
        // L'effet est couple a une ref (garde de pagination, generation de fetch) :
        // ecrire cette ref pendant le rendu serait une autre violation. On garde l'effet.
        // eslint-disable-next-line react/set-state-in-effect
        setSelectedCountry(country);
        setLocalNumber(formData.phone.slice(country.dialCode.length));
      } else {
        setLocalNumber(formData.phone);
      }
    }
  }, [formData.phone]);

  return (
    <div className="signup__step-content">
      <h2 className="signup__step-title">{stepTitle}</h2>
      <p className="signup__step-subtitle">Nom & Prénom</p>
      <FormContainer twoColumnsMax>
        <Form.Item label="Prénom" layout="vertical">
          <Input
            value={formData.firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            placeholder="Jean"
            autoFocus
          />
        </Form.Item>
        <Form.Item label="Nom" layout="vertical">
          <Input
            value={formData.lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            placeholder="Dupont"
            autoFocus
          />
        </Form.Item>
      </FormContainer>
      <FormContainer oneColumnMax>
        {/* <p className="signup__step-subtitle">Celle-ci sera utilisée pour le compte</p> */}
        <Form.Item label="Adresse email" layout="vertical" validateStatus={showError ? 'error' : undefined} help={showError ? 'Format d\'email invalide' : undefined}>
          <Input
            value={formData.email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="jean.dupont@email.com"
            autoFocus
          />
        </Form.Item>
      </FormContainer>
      <FormContainer oneColumnMax>
        <Form.Item label="Numéro de téléphone" layout="vertical" validateStatus={localNumber.length > 0 && localNumber.length < selectedCountry.maxDigits ? 'error' : undefined} help={localNumber.length > 0 && localNumber.length < selectedCountry.maxDigits ? 'Le numéro de téléphone doit contenir ' + selectedCountry.maxDigits + ' chiffres' : undefined}>
          <Flex gap="8px">
            <Select
              style={{ width: 'min-content' }}
              options={COUNTRIES.map((country) => ({
                label: country.flag + ' ' + country.dialCode,
                value: country.code,
                country: country,
              }))}
              value={selectedCountry.code}
              onChange={(value, option) => option && handleCountryChange((option as unknown as { country: Country }).country)}
              optionRender={(option) => (
                <Flex gap="8px">
                  <span>{(option.data.country.flag)}</span>
                  <span>{(option.data.country.name)}</span>
                  <span style={{ marginLeft: 'auto' }}>{(option.data.country.dialCode)}</span>
                </Flex>
              )}
              popupMatchSelectWidth={false}
            />

            <Input
              value={localNumber}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNumberChange(e.target.value)
              }}
              onPaste={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleNumberChange(e.currentTarget.value + e.clipboardData.getData('text'))
              }}
              placeholder={`${selectedCountry.code === 'FR' ? '612345678' : ''}`}
              maxLength={selectedCountry.maxDigits}
              autoFocus
              allowClear
              status={localNumber.length > 0 && localNumber.length < selectedCountry.maxDigits ? 'error' : undefined}
            />
          </Flex>
        </Form.Item>
      </FormContainer>
    </div>
  );
};

export default StepPersonalInfo;
